/**
 * @name ResponsibilitiesProgress
 * @description Componente de avance de responsabilidades para supervisores y tercer nivel en configuración de perfil
 * @author Ricardo Guerra
 */

import React, { PureComponent } from 'react';
import i18n from 'i18next';
import styled from 'styled-components';

import { EmployeeService } from '@services';
import { FiltersExpansion, Report } from '@components';
import { ResponsibilitiesService, ResponseHandlerService } from '@services';
import { Flex, Icons, MUI, Unicons, Typography } from '@styles';

import { trackPromise } from 'react-promise-tracker';
import { CSVLink } from 'react-csv';

class ResponsibilitiesProgress extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      reportConfig: {
        rows: [],
        columns: [
          { name: 'Nombre', header: i18n.t('GenericMessages.name'), visible: true, enableHiding: true, minWidth: 200, defaultFlex: 1 },
          { name: 'Porcentaje', header: i18n.t('GenericMessages.percentage'), visible: true, enableHiding: true, minWidth: 200, defaultFlex: 1 },
          { name: 'Estatus', header: i18n.t('GenericMessages.status'), visible: true, enableHiding: true, minWidth: 200, defaultFlex: 1 }
        ]
      },
      structure: [],
      filters: {
        employee: null,
        status: 0
      },
      structureResponsibilities: []
    };
  }

  componentDidMount() {
    const { filters } = this.state;
    this.getStructure();
    this.getProgressReport(filters);
    this.getStructureReport();
  }

  /**
   * Consulta la estructura del supervisor o tercer nivel
   */
  getStructure = () => {
    const configFilter = {
      tipoEstructura: true,
      estructuraDirecta: true
    };

    EmployeeService.getStructure({ page: 0, size: 0, filter: configFilter })
      .then((response) => {
        this.setState({ structure: response });
      })
      .catch(() => { });
  }

  /** 
   * Actualiza los filtros del reporte
   * @param filters (employee, status)
   */
  updateFilters = ({ employee, status }) => {
    const filters = {
      employee: employee ? employee.IdEmpleado : null,
      status: status !== 0 ? status : null
    };

    this.getProgressReport(filters);
    this.setState({ filters: { employee, status } });
  }

  /**
   * Consulta el reporte de avance de responsabilidades
   */
  getProgressReport = (filters) => {
    trackPromise(
      ResponsibilitiesService.getProgressReport({
        page: 0,
        size: 0,
        filter: {
          IdEmpleado: filters.employee,
          Id: filters.status
        }
      })
        .then((response) => {
          const { reportConfig } = this.state;
          reportConfig.rows = response;
          this.setState(({ reportConfig }) => ({
            reportConfig: { ...reportConfig, rows: response }
          }));
        })
        .catch(() => {
          // No encuentra registros (Error 404).
          this.setState(({ reportConfig }) => ({
            reportConfig: { ...reportConfig, rows: [] }
          }));
        }),
      'loading-report'
    );
  }

  /*
   * Consulta el reporte de responsabilidades de la estructura
   */
  getStructureReport = () => {
    ResponsibilitiesService.getStructureReport()
      .then((response) => {
        this.setState({ structureResponsibilities: response });
      })
      .catch((error) => {
        ResponseHandlerService.error(error);
      });
  }

  render() {
    const {
      filters,
      reportConfig,
      structure,
      structureResponsibilities
    } = this.state;

    return (
      <>
        <Flex.Row alignItems="flex-start">
          {/* Filtros */}
          <Flex.Row flex="1">
            <Styled.BoxFilters>
              <FiltersExpansion>
                <MUI.Box padding="8px" width="100%">
                  <MUI.Grid container alignItems="center" spacing={4}>
                    {/* Empleado */}
                    <MUI.Grid item md={4} sm={6} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.Autocomplete
                          label={i18n.t('GenericMessages.employee')}
                          searchList={structure}
                          searchBy="Nombre"
                          value={filters.employee}
                          onChange={(newEmployee) => this.updateFilters({
                            employee: newEmployee,
                            status: filters.status
                          })}
                        />
                      </Styled.BoxFilter>
                    </MUI.Grid>
                    {/* Estatus */}
                    <MUI.Grid item md={4} sm={6} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.TextFieldIcon
                          select
                          label={i18n.t('GenericMessages.status')}
                          type="text"
                          name="status"
                          value={filters.status}
                          InputProps={{ startAdornment: (<Icons.FilterList />) }}
                          onChange={(event) => this.updateFilters({
                            employee: filters.employee,
                            status: event.target.value
                          })}
                        >
                          <MUI.SelectOption value={0}>
                            {i18n.t('GenericMessages.all')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={1}>
                            {i18n.t('StatusText.active')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={2}>
                            {i18n.t('StatusText.toBeApproved')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={3}>
                            {i18n.t('StatusText.approved')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={4}>
                            {i18n.t('StatusText.rejected')}
                          </MUI.SelectOption>
                        </MUI.TextFieldIcon>
                      </Styled.BoxFilter>
                    </MUI.Grid>
                  </MUI.Grid>
                </MUI.Box>
              </FiltersExpansion>
            </Styled.BoxFilters>
          </Flex.Row>

          {/* Descargar */}
          {structureResponsibilities.length !== 0 ? (
            <Flex.Row padding="8px">
              <Unicons.Download size="28" style={{ marginRight: '8px' }} />
              <Flex.Col>
                <Typography.Text>{i18n.t('Responsibilities.respReport')}</Typography.Text>
                <Styled.CSVLink
                  headers={[
                    { label: i18n.t('GenericMessages.name'), key: 'Nombre' },
                    { label: i18n.t('GenericMessages.employeeCode'), key: 'CodigoEmpleado' },
                    { label: i18n.t('GenericMessages.area'), key: 'Area' },
                    { label: i18n.t('GenericMessages.supervisor'), key: 'Supervisor' },
                    { label: i18n.t('Responsibilities.responsibility'), key: 'Responsabilidad' },
                    { label: i18n.t('GenericMessages.weight'), key: 'Peso' },
                    { label: i18n.t('GenericMessages.status'), key: 'Estatus' }
                  ]}
                  data={structureResponsibilities}
                  filename={`${i18n.t('Responsibilities.structureResponsibilities')}.csv`}
                >
                  <Flex.Row>
                    <Typography.Text color="blueDark">
                      {i18n.t('GenericMessages.download')}
                      <Typography.Small color="greyDarkAlpha">{` - ${i18n.t('Responsibilities.structureResponsibilities')}.csv`}</Typography.Small>
                    </Typography.Text>
                  </Flex.Row>
                </Styled.CSVLink>
              </Flex.Col>
            </Flex.Row>
          ) : (
            <Flex.Row padding="8px">
              <Unicons.CircleExclamation size="28" style={{ marginRight: '8px' }} />
              <Flex.Col>
                <Typography.Text>{i18n.t('Responsibilities.respReport')}</Typography.Text>
                <Typography.Small color="greyDarkAlpha">{i18n.t('Responsibilities.noRespReport')}</Typography.Small>
              </Flex.Col>
            </Flex.Row>
          )}
        </Flex.Row>

        {/* Reporte */}
        <MUI.Box padding="8px">
          <Report
            columns={reportConfig.columns}
            rows={reportConfig.rows}
            rowsLength={reportConfig.rows.length}
            exportToCSV={true}
            exportFileName={i18n.t('GenericMessages.respProgressTab')}
            styles={{ height: 'calc(100vh - 350px)' }}
          />
        </MUI.Box>
      </>
    );
  }
}

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.BoxFilters = styled.div`
  width: 100%;
  margin-bottom: 8px;
`;

Styled.BoxFilter = styled.div`
  .MuiFormControl-root {
    min-height: 46px;
  }
`;

Styled.CSVLink = styled(CSVLink)`
  display: flex;
  color: ${(props) => props.theme.colors.blueDark};
  text-decoration: none;
  &:hover {text-decoration: underline; }
`;

export default ResponsibilitiesProgress;